import React, { useEffect, useState, useContext } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import ConfirmDialog from "./dialog";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import { AuthContext } from "contexts/AuthProvider";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";

const StyledMobileNavbar = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: "20rem",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: "5rem 0 5rem 0",
    overflowY: "auto",
    maxHeight: "100%",
  },
}));

const MobileNavbar = ({
  drawerOpen,
  handleDrawerToggle,
  menus,
  currentUser,
  pathname,
  quoteCount,
  // [보류] 견적 문의 카트 비활성화
  // inquiryCount,
  isAdmin,
}) => {
  const { signOut } = useContext(AuthContext);
  const [openSubMenu, setOpenSubMenu] = useState({});
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const navigate = useNavigate();
  const handleClick = (key) => {
    setOpenSubMenu((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleDrawerToggle();
  };
  const handleLogout = () => {
    signOut();
    handleNavigation("/signin");
    setOpenLogoutDialog(false);
  };

  const handleOpenLogoutDialog = () => {
    setOpenLogoutDialog(true);
  };

  const handleCloseLogoutDialog = () => {
    setOpenLogoutDialog(false);
  };

  useEffect(() => {
    if (drawerOpen) {
      setOpenSubMenu({});
      handleDrawerToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderMenuItems = (items, level = 0) => (
    <List component="div" disablePadding>
      {items.map((item) => (
        <React.Fragment key={item.key}>
          <ListItemButton
            sx={{
              pl: (level + 1) * 4,
              my: 0.5,
              "&:not(:last-child)": {
                mb: 1,
              },
              color: pathname === item.to ? "primary.orange" : "inherit",
            }}
            onClick={() => {
              // 자체 페이지가 있으면 이동하고, 없으면 하위 메뉴만 펼친다.
              // 대표 페이지를 가진 카테고리는 둘 다 해당하므로 이동을 우선한다.
              if (item.to) {
                handleNavigation(item.to);
              } else if (item.children) {
                handleClick(item.key);
              }
            }}
          >
            <ListItemText
              primary={item.name}
              secondary={item.englishName}
              secondaryTypographyProps={{
                sx: { color: "rgba(255, 255, 255, 0.55)", fontSize: "0.7rem" },
              }}
            />
            {item.children && (
              // 이동과 펼치기를 모두 할 수 있도록 화살표를 별도 버튼으로 둔다.
              <IconButton
                size="small"
                aria-label={openSubMenu[item.key] ? "하위 메뉴 접기" : "하위 메뉴 펼치기"}
                sx={{ color: "inherit" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick(item.key);
                }}
              >
                {openSubMenu[item.key] ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </ListItemButton>

          {item.children && (
            <Collapse in={!!openSubMenu[item.key]} timeout="auto" unmountOnExit>
              {renderMenuItems(item.children, level + 1)}
            </Collapse>
          )}
        </React.Fragment>
      ))}
    </List>
  );

  return (
    <StyledMobileNavbar
      anchor="right"
      open={drawerOpen}
      onClose={handleDrawerToggle}
    >
      {renderMenuItems(menus)}
      <List>
        {!isAdmin && (
          <ListItemButton
            sx={{
              pl: 4,
              my: 0.5,
              "&:not(:last-child)": {
                mb: 1,
              },
              color: pathname === "/cart/quote" ? "primary.orange" : "inherit",
            }}
            onClick={() => {
              handleNavigation("/cart/quote");
            }}
          >
            <ListItemText
              primary="장바구니"
              secondary="Cart"
              secondaryTypographyProps={{
                sx: { color: "rgba(255, 255, 255, 0.55)", fontSize: "0.7rem" },
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                ml: 1,
                position: "relative",
              }}
            >
              <ShoppingBagIcon sx={{ fontSize: "20px" }} />
              {quoteCount > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    backgroundColor: "primary.orange",
                    borderRadius: "50%",
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "white",
                  }}
                >
                  <Typography
                    variant="small"
                    fontWeight="bold"
                    fontSize="0.7rem"
                  >
                    {quoteCount > 99 ? "99+" : quoteCount}
                  </Typography>
                </Box>
              )}
            </Box>
          </ListItemButton>
        )}
        <ListItemButton
          sx={{
            pl: 4,
            my: 0.5,
            "&:not(:last-child)": {
              mb: 1,
            },
            color: pathname === "/my-inquiry" ? "primary.orange" : "inherit",
          }}
          onClick={() => {
            handleNavigation("/my-inquiry");
          }}
        >
          <ListItemText
            primary={isAdmin ? "견적 문의 리스트" : "제출된 견적 문의"}
          />
        </ListItemButton>
        <ListItemButton
          sx={{
            pl: 4,
            my: 0.5,
            "&:not(:last-child)": {
              mb: 1,
            },
            color: pathname === "/my-quote" ? "primary.orange" : "inherit",
          }}
          onClick={() => {
            handleNavigation("/my-quote");
          }}
        >
          <ListItemText
            primary={isAdmin ? "발주 문의 리스트" : "제출된 발주 문의"}
          />
        </ListItemButton>
      </List>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          display: "flex",
          alignItems: "center",
          zIndex: 1000,
          gap: 1,
          padding: 1,
          backgroundColor: "primary.main",
          width: "100%",
          borderTop: "1px solid rgba(255, 255, 255, 0.2)",
          maxWidth: "20rem",
          cursor: currentUser ? "pointer" : "pointer",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: currentUser
              ? "rgba(255, 255, 255, 0.1)"
              : "primary.main",
          },
          "&:active": {
            backgroundColor: currentUser
              ? "rgba(255, 255, 255, 0.15)"
              : "primary.main",
          },
        }}
        onClick={
          currentUser
            ? () => handleNavigation("/profile")
            : () => handleNavigation("/signin")
        }
      >
        <AccountCircleIcon
          sx={{
            fontSize: "40px",
            color: pathname === "/profile" ? "primary.orange" : "inherit",
          }}
        />
        {currentUser ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
            }}
          >
            <Typography
              variant="body"
              sx={{
                color: pathname === "/profile" ? "primary.orange" : "inherit",
              }}
            >
              {currentUser?.company}
            </Typography>
            <Typography variant="small">{currentUser?.email}</Typography>
          </Box>
        ) : (
          <Typography variant="body">로그인</Typography>
        )}
        {currentUser && (
          <LogoutIcon
            sx={{
              cursor: "pointer",
              fontSize: "30px",
              marginLeft: "auto",
              "&:hover": {
                opacity: 0.8,
              },
            }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering parent onClick
              handleOpenLogoutDialog();
            }}
          />
        )}
      </Box>
      <ConfirmDialog
        open={openLogoutDialog}
        title="Confirm Logout"
        message="로그아웃 하시겠습니까?"
        onClose={handleCloseLogoutDialog}
        onConfirm={handleLogout}
        confirmText="확인"
        cancelText="취소"
      />
    </StyledMobileNavbar>
  );
};

export default MobileNavbar;
